import React, { useEffect, useRef, useState } from 'react';
import { FaHeadphones, FaLightbulb, FaClipboardCheck, FaSearch } from 'react-icons/fa';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import httpService from '../../services/httpService';

const TABS = [
    { type: 'episode',      label: 'Episodes',      icon: <FaHeadphones /> },
    { type: 'key_takeaway', label: 'Key Takeaways',  icon: <FaLightbulb /> },
    { type: 'practice',     label: 'My Practices',   icon: <FaClipboardCheck /> },
];

/**
 * TagPicker — floating dropdown that lets the user search and select a club
 * element to attach to a chat message.
 *
 * Props:
 *   clubGuid      string
 *   listenerGuid  string — scopes practice results to the listener in this thread
 *   onSelect      ({ type, guid, displayText }) => void
 *   onClose       () => void
 */
export default function TagPicker({ clubGuid, listenerGuid, onSelect, onClose }) {
    const [activeTab, setActiveTab] = useState('episode');
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const searchTimeout = useRef(null);

    // Fetch items whenever tab or search changes
    useEffect(() => {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            fetchItems();
        }, 250);
        return () => clearTimeout(searchTimeout.current);
    }, [activeTab, search, clubGuid]);

    const fetchItems = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ type: activeTab });
            if (search) params.append('search', search);
            const res = await httpService.get(`chat/club/${clubGuid}/listener/${listenerGuid}/taggable?${params}`);
            setItems(res.data?.data ?? []);
        } catch {
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = (item) => {
        const displayText = item.title ?? item.display_text ?? 'Item';
        onSelect({ type: activeTab, guid: item.guid, displayText });
        onClose();
    };

    const itemList = (
        <div className={'py-3'}>
            <div className="tag-picker__search">
                <FaSearch className="tag-picker__search-icon" />
                <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />
            </div>

            <div className="tag-picker__list">
                {isLoading && <div className="tag-picker__empty">Loading...</div>}
                {!isLoading && items.length === 0 && (
                    <div className="tag-picker__empty">No items found</div>
                )}
                {!isLoading && items.map((item) => (
                    <button
                        key={item.guid ?? item.id}
                        className="tag-picker__item"
                        onClick={() => handleSelect(item)}
                    >
                        {activeTab === 'episode' && item.color && (
                            <span
                                className="tag-picker__item-color"
                                style={{ backgroundColor: item.color }}
                            />
                        )}
                        <span className="tag-picker__item-title">{item.title ?? 'Untitled'}</span>
                        {activeTab === 'key_takeaway' && item.insight && (
                            <span className="tag-picker__item-sub">
                                {item.insight.slice(0, 60)}{item.insight.length > 60 ? '…' : ''}
                            </span>
                        )}
                        {activeTab === 'practice' && (
                            <span className="tag-picker__item-sub">
                                {item.completed ? '✓ Completed' : 'In progress'}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="tag-picker py-3">
            <Tabs
                activeKey={activeTab}
                onSelect={(k) => { setActiveTab(k); setSearch(''); }}
                className="mb-0"
            >
                {TABS.map((tab) => (
                    <Tab key={tab.type} eventKey={tab.type} title={<span>{tab.icon} {tab.label}</span>}>
                        {activeTab === tab.type && itemList}
                    </Tab>
                ))}
            </Tabs>
        </div>
    );
}
